import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import httpClient from '../services/http-client';
import {
  DivisionOrderAttributeTemplateDto,
  CreateDivisionOrderAttributeTemplateRequest,
  UpdateDivisionOrderAttributeTemplateRequest,
} from '../types';

const QUERY_KEY = 'division-order-attribute-templates';

export function useDivisionOrderAttributeTemplates(
  divisionId: string,
  enabled: boolean = true
) {
  return useQuery<DivisionOrderAttributeTemplateDto[]>({
    queryKey: [QUERY_KEY, divisionId],
    queryFn: async () => {
      return await httpClient.get<DivisionOrderAttributeTemplateDto[]>(
        `/api/v1/divisions/${divisionId}/order-attribute-templates`
      );
    },
    enabled: enabled && !!divisionId,
  });
}

export function useCreateDivisionOrderAttributeTemplate(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    DivisionOrderAttributeTemplateDto,
    Error,
    CreateDivisionOrderAttributeTemplateRequest
  >({
    mutationFn: async (request) => {
      return await httpClient.post<DivisionOrderAttributeTemplateDto>(
        `/api/v1/divisions/${divisionId}/order-attribute-templates`,
        request
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, divisionId] });
    },
  });
}

export function useUpdateDivisionOrderAttributeTemplate(
  divisionId: string,
  templateId: string
) {
  const queryClient = useQueryClient();

  return useMutation<
    DivisionOrderAttributeTemplateDto,
    Error,
    UpdateDivisionOrderAttributeTemplateRequest
  >({
    mutationFn: async (request) => {
      return await httpClient.put<DivisionOrderAttributeTemplateDto>(
        `/api/v1/divisions/${divisionId}/order-attribute-templates/${templateId}`,
        request
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, divisionId] });
    },
  });
}

export function useDeleteDivisionOrderAttributeTemplate(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (templateId: string) => {
      await httpClient.delete(
        `/api/v1/divisions/${divisionId}/order-attribute-templates/${templateId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, divisionId] });
    },
  });
}
